import React                      from 'react';
import { observer }               from 'mobx-react-lite';
import View                       from '@widesk-ui/components/View';
import Alert                      from '@widesk-ui/components/Alert';
import Button                     from '@widesk-ui/components/Button';
import Radio                      from '@widesk-ui/components/form/Radio';
import { Modal }                  from '@widesk-ui/components/modal/Modal';
import { message }                from '@widesk-ui/hooks/useMessage';
import ApiCollection              from '@widesk-core/models/ApiCollection';
import PlaceModel                 from '@models/map/PlaceModel';
import PlaceTypeModel             from '@models/map/PlaceTypeModel';
import SelectPlaceType            from '@selects/map/SelectPlaceType';
import Field                      from '@/TokamapEditor/ux/AreaSidebar/Field';
import currentEditorStore         from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore  from '@/TokamapEditor/stores/currentProjectEditorStore';
import leftSidebarStore           from '@/TokamapEditor/stores/leftSidebarStore';
import { Theme }                  from '@/TokamapEditor/stores/themeStore';
import useDesignTokens            from '@/hooks/useDesignTokens';
import { Shape }                  from '@tokamap/core';

type Scope = 'all' | 'placeType';

const plural = (count: number, suffix = 's') => count > 1 ? suffix : '';

/**
 * Applique un thème à un lot de formes d'un coup, depuis la liste des thèmes.
 *
 * Deux limites structurelles, affichées à l'utilisateur plutôt que masquées :
 *  - la portée est la CARTE OUVERTE, pas le projet : l'éditeur n'a que ses formes en mémoire, alors
 *    que les thèmes sont au niveau projet ;
 *  - les formes-image (`shapeKind: "image"`) sont exclues — elles portent leur propre rendu, un
 *    thème de couleurs n'aurait aucun effet visible dessus (voir `resolveShapeVisualProps`), même
 *    exclusion que le compteur de `PlacesWithoutShapeButton`.
 */
const ThemeApplyForm = observer(({ theme, onApplied }: { theme: Theme; onApplied?: () => void; }) => {
	const [scope, setScope] = React.useState<Scope>('all');
	const [placeTypeIds, setPlaceTypeIds] = React.useState<id[]>([]);
	const [targets, setTargets] = React.useState<Shape[]>([]);
	const [counting, setCounting] = React.useState(false);
	const tokens = useDesignTokens();

	const project = currentProjectEditorStore.get.project;

	// Décompte tenu à jour à chaque changement de portée, pour que l'utilisateur voie combien de
	// formes seront touchées AVANT de cliquer. Les formes résolues ici sont réutilisées telles quelles
	// à l'application : inutile de refaire la requête, et un id devenu obsolète entre-temps est
	// simplement ignoré par `setShapeThemeId`.
	React.useEffect(() => {
		let ignore = false;

		if (scope === 'placeType' && !placeTypeIds.length) {
			setTargets([]);
			setCounting(false);
			return;
		}

		setCounting(true);

		resolveTargetShapes(scope, placeTypeIds, project.id)
			.then(shapes => {
				if (ignore) return;
				setTargets(shapes);
				setCounting(false);
			})
			.catch(err => {
				if (ignore) return;
				setTargets([]);
				setCounting(false);
				message.exception(err);
			});

		// Une réponse plus lente d'une sélection précédente ne doit pas écraser le décompte courant :
		// l'utilisateur peut changer de types pendant la requête.
		return () => {
			ignore = true;
		};
	}, [scope, placeTypeIds, project.id]);

	const count = targets.length;
	const alreadyThemed = targets.filter(shape => shape.themeId && shape.themeId !== theme.id).length;

	const apply = () => Modal.confirm({
		okText: 'Appliquer',
		title: `Appliquer "${theme.name}" à ${count} forme${plural(count)} ?`,
		content: `Leur apparence propre sera remplacée par celle du thème. Elle est conservée et reviendra si vous les déliez.`,
		onOk: async () => {
			try {
				// Un seul appel : `setShapeThemeId` groupe tout en une entrée d'historique et un seul
				// `onSave` (voir `runShapeMutation` dans `@tokamap/core`), donc une seule requête.
				currentEditorStore.get.setShapeThemeId(targets.map(shape => shape.id), theme.id);

				message.success(`Thème appliqué à ${count} forme${plural(count)}.`);
				onApplied?.();
				leftSidebarStore.closeLower();
			} catch (err) {
				message.exception(err);
			}
		},
	});

	return (
		<View gap="md">
			<Alert message="L'application ne concerne que les formes de la carte ouverte." />

			<Field label="Appliquer à">
				<Radio.Group
					optionType="default"
					vertical
					value={scope}
					onChange={e => setScope((e as any).target.value as Scope)}
				>
					<Radio value="all">Toutes les formes de la carte</Radio>
					<Radio value="placeType">
						Les formes liées à un {PlaceTypeModel.localize('default', 'l')}
					</Radio>
				</Radio.Group>
			</Field>

			{scope === 'placeType' && (
				<Field label={PlaceTypeModel.localize('plural')}>
					<View gap="xs">
						<SelectPlaceType
							identifier="id"
							multiple
							projectUrn={project.urn}
							value={placeTypeIds}
							onChange={value => setPlaceTypeIds((value || []) as id[])}
						/>

						<View color={tokens.text.muted} size={12.5}>
							Seules les formes dont l'action ouvre un point d'intérêt de ces types seront liées.
						</View>
					</View>
				</Field>
			)}

			<View gap={4} bold size={13.5}>
				{counting ? 'Calcul du nombre de formes…' : count === 0 ? (
					'Aucune forme ne correspond à cette sélection.'
				) : (
					`${count} forme${plural(count)} ser${count > 1 ? 'ont' : 'a'} liée${plural(count)} à ce thème.`
				)}

				{!counting && !!alreadyThemed && (
					<View color={tokens.text.muted} size={12.5} style={{ fontWeight: 400 }}>
						Dont {alreadyThemed} déjà liée{plural(alreadyThemed)} à un autre thème, qui changera{plural(alreadyThemed, 'ont')} de thème.
					</View>
				)}
			</View>

			<Button
				type="primary"
				loading={counting}
				disabled={!count}
				onClick={apply}
			>
				Appliquer
			</Button>
		</View>
	);
});

/**
 * Formes visées par le lot. Pour `placeType` on résout d'abord les points d'intérêt de ces types
 * (une forme ne porte que l'id du point d'intérêt qu'elle ouvre, jamais son type), puis on retient
 * les formes qui pointent vers l'un d'eux.
 */
async function resolveTargetShapes(scope: Scope, placeTypeIds: id[], projectId: id): Promise<Shape[]> {
	const shapes = currentEditorStore.get.getAllShapes().filter(shape => shape.shapeKind !== 'image');

	if (scope === 'all') return shapes;

	// `ApiCollection` sans `itemsPerPage` plutôt qu'une `PagedCollection` : sans ce paramètre,
	// `ApiConnectorV4` enchaîne lui-même les pages jusqu'au total, on récupère donc TOUS les points
	// d'intérêt de ces types et pas seulement la première page. Le garde-fou `MAX_RESULTS` d'
	// `ApiCollection` (1000) lève au-delà, comme partout ailleurs dans l'app.
	const collection = new ApiCollection(PlaceModel);
	collection.setRequiredFilter('project', projectId);
	collection.setFilter('placeType', placeTypeIds as never);
	await collection.list();

	const placeIds = new Set(collection.map(place => String(place.id)));

	return shapes.filter(shape => (
		shape.action === 'open_place'
		&& placeIds.has(String((shape.entity as any)?.entity?.id))
	));
}

export default ThemeApplyForm;
